import {
	BeCountView,
	BeIconView,
	type BeIconViewItem,
	BeMenuBar,
	BeMenuBarItem,
	BeRow,
	BeScrollView,
	BeStack,
	BeTable,
	type BeTableColumn,
	type BeTableRow,
	BeWindow,
} from "@beos-css/react/index.ts";
import { useState } from "react";

import iconBeide from "../../icons/beide.png";
import iconBeide32 from "../../icons/beide-32.png";
import iconPeople from "../../icons/people.png";
import iconPeople32 from "../../icons/people-32.png";
import iconQuickres from "../../icons/quickres.png";
import iconQuickres32 from "../../icons/quickres-32.png";
import iconSoundRecorder from "../../icons/soundrecorder.png";
import iconSoundRecorder32 from "../../icons/soundrecorder-32.png";
import iconStyledEdit from "../../icons/stylededit.png";
import iconStyledEdit32 from "../../icons/stylededit-32.png";
import iconTerminal from "../../icons/terminal.png";
import iconTerminal32 from "../../icons/terminal-32.png";

const columns: BeTableColumn[] = [
	{ id: "name", title: "Name", width: 170, sortable: true },
	{ id: "size", title: "Size", width: 80, align: "right" },
	{ id: "kind", title: "Kind", width: 190 },
	{ id: "date", title: "Date Modified", width: 160 },
];

const rows: BeTableRow[] = [
	{
		id: "beide",
		icon: { mini: iconBeide, large: iconBeide32 },
		cells: { name: "BeIDE", size: "1.2 MB", kind: "Application", date: "Jan 2, 1999, 04:30 PM" },
	},
	{
		id: "terminal",
		icon: { mini: iconTerminal, large: iconTerminal32 },
		cells: { name: "Terminal", size: "256 KB", kind: "Application", date: "Nov 5, 1998, 10:15 AM" },
	},
	{
		id: "people",
		icon: { mini: iconPeople, large: iconPeople32 },
		cells: { name: "People", size: "180 KB", kind: "Application", date: "Oct 21, 1999, 02:45 PM" },
	},
	{
		id: "quickres",
		icon: { mini: iconQuickres, large: iconQuickres32 },
		cells: { name: "QuickRes", size: "210 KB", kind: "Application", date: "Aug 14, 1998, 11:20 AM" },
	},
	{
		id: "soundrec",
		icon: { mini: iconSoundRecorder, large: iconSoundRecorder32 },
		cells: { name: "SoundRecorder", size: "148 KB", kind: "Application", date: "Sep 30, 1999, 09:00 AM" },
	},
	{
		id: "stylededit",
		icon: { mini: iconStyledEdit, large: iconStyledEdit32 },
		cells: { name: "StyledEdit", size: "324 KB", kind: "Application", date: "Dec 12, 1998, 05:10 PM" },
	},
];

/* the same items, drawn as poses instead of rows -- #634's whole point */
const items: BeIconViewItem[] = rows.map(({ id, icon, cells }) => ({ id, icon, name: cells.name }));

const FRAME = { width: 450, height: 160 };
const VIEW_LABEL = { list: "List View", icon: "Icon View", mini: "Mini Icon View" } as const;

/**
 * One Tracker document window. `variant` decides who owns the frame -- a
 * resizable window sizes its own content and the scroll view fills it, where a
 * fixed one lets the scroll view state the size. `view` picks which of
 * BPoseView's three modes draws the same items.
 */
function TrackerWindow({
	variant,
	view = "list",
	selectedIds,
	onSelectItem,
}: {
	variant: "resizable" | "fixed";
	view?: keyof typeof VIEW_LABEL;
	selectedIds: string[];
	onSelectItem: (id: string) => void;
}) {
	const resizable = variant === "resizable";
	return (
		<BeWindow
			title={`people (${view === "list" ? (resizable ? "Resizable" : "Fixed") : VIEW_LABEL[view]})`}
			onClose={() => {}}
			onZoom={() => {}}
			resizable={resizable ? "knob" : undefined}
			flush
			contentStyle={resizable ? FRAME : undefined}
		>
			<BeStack fill gap="none">
				<BeMenuBar aria-label="Tracker">
					{["File", "Window", "Attributes"].map((name) => (
						<BeMenuBarItem key={name}>{name}</BeMenuBarItem>
					))}
				</BeMenuBar>

				<BeScrollView
					horizontal
					vertical
					corner
					border="none"
					status={<BeCountView count={rows.length} />}
					fill={resizable}
					contentStyle={resizable ? undefined : FRAME}
				>
					<div>
						{view === "list" ? (
							<BeTable
								splitHeader
								nameSelection
								fillTrailing={resizable}
								striped={!resizable}
								columns={columns}
								rows={rows}
								sort={{ columnId: "name", ascending: true }}
								onSort={() => {}}
								selectedIds={selectedIds}
								onSelectItem={onSelectItem}
							/>
						) : (
							<BeIconView mode={view} items={items} selectedIds={selectedIds} onSelectItem={onSelectItem} />
						)}
					</div>
				</BeScrollView>
			</BeStack>
		</BeWindow>
	);
}

export function ScrollViewDemo() {
	const [tableSel, setTableSel] = useState<string[]>([]);
	const select = (id: string) => setTableSel([id]);

	return (
		<section id="demo-scroll-view">
			<h2>Scroll view (BScrollView) -- the Tracker window</h2>
			<p>
				The full document-window composition: pose view + both scrollbars, the BCountView strip sharing the bottom row, and the corner gap.
				Scrolling is live: wheel the list or drag the bars. The lists use the R5 header split, so the column edge cuts through the icon; the
				other two windows draw the same items in Tracker's Icon and Mini Icon views, and selection follows across all four. (The resize knob
				is demoed on its own below -- coupling a drag-resize window to a fixed pose view fights the scroll ranges.)
			</p>
			<BeRow wrap gap="row">
				<TrackerWindow variant="resizable" selectedIds={tableSel} onSelectItem={select} />
				<TrackerWindow variant="fixed" selectedIds={tableSel} onSelectItem={select} />
				<TrackerWindow variant="fixed" view="icon" selectedIds={tableSel} onSelectItem={select} />
				<TrackerWindow variant="fixed" view="mini" selectedIds={tableSel} onSelectItem={select} />
				<BeScrollView horizontal vertical border="fancy" contentStyle={{ width: 220, height: 120 }}>
					{/* No font-size override: this section renders in the control role
					    (Coverage 10), and asking for 12px scaled a 10px-designed face
					    by 1.2, putting every stem edge off the pixel grid. */}
					<div className="demo-overflow-text">
						A B_FANCY_BORDER BScrollView: the 2px sunken ring
						<br />
						(#b8/#98 top-left, white bottom-right), scrollbars riding
						<br />
						the inner ring, no corner gap unless asked. This filler
						<br />
						text is wider and taller than the frame so both bars
						<br />
						have a real range to work with. BeOS is the media OS.
						<br />
						Line six. Line seven. Line eight. Line nine. Line ten.
					</div>
				</BeScrollView>
				<BeWindow title="Resize me" onClose={() => {}} onZoom={() => {}} resizable contentStyle={{ width: 220, height: 130 }}>
					A resizable <em>titled</em> window (not a document window) gets SBeDecor's resize <strong>notch</strong>, not the dot-grid knob:
					two #989898 ticks etched into the inner border 22px in from the bottom-right corner, bracketing the resize grab-zone. Drag the
					corner to resize.
				</BeWindow>
			</BeRow>
		</section>
	);
}
